import { useState, type FormEvent } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ApiError } from '../lib/api'
import { useAuth } from '../lib/auth'
import TrafficPanel from '../components/TrafficPanel'

/**
 * Sign in / create account.
 *
 * This page is also Module 8's specimen: the traffic panel underneath shows the
 * actual request you just made and the actual response — including the fact
 * that no token appears anywhere in the JSON, because it left in a Set-Cookie
 * header your JavaScript is not allowed to see.
 */
export default function Login() {
  const { login, register } = useAuth()
  const navigate = useNavigate()
  const location = useLocation() as { state?: { from?: string } }

  const [mode, setMode] = useState<'login' | 'register'>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    setFieldErrors({})

    try {
      if (mode === 'login') {
        await login(email, password)
      } else {
        await register(email, password, displayName.trim() || undefined)
      }
      navigate(location.state?.from ?? '/', { replace: true })
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message)
        if (err.details) setFieldErrors(err.details)
      } else {
        setError('Something went wrong. Try again.')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'login'}
            className={mode === 'login' ? 'tab tab-active' : 'tab'}
            onClick={() => setMode('login')}
          >
            Sign in
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'register'}
            className={mode === 'register' ? 'tab tab-active' : 'tab'}
            onClick={() => setMode('register')}
          >
            Create account
          </button>
        </div>

        <form onSubmit={onSubmit} noValidate>
          {mode === 'register' && (
            <label className="field">
              <span>Display name <span className="dim">(optional)</span></span>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                autoComplete="nickname"
                maxLength={60}
              />
            </label>
          )}

          <label className="field">
            <span>Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
              aria-invalid={Boolean(fieldErrors.email)}
            />
            {fieldErrors.email && <em className="field-error">{fieldErrors.email}</em>}
          </label>

          <label className="field">
            <span>Password</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              required
              aria-invalid={Boolean(fieldErrors.password)}
            />
            {fieldErrors.password && <em className="field-error">{fieldErrors.password}</em>}
            {mode === 'register' && !fieldErrors.password && (
              <em className="field-hint">
                At least 10 characters. Length beats symbols — a passphrase is fine.
              </em>
            )}
          </label>

          {error && (
            <div className="error-panel" role="alert">
              {error}
            </div>
          )}

          <button type="submit" className="button button-primary button-full" disabled={busy}>
            {busy ? 'Working…' : mode === 'login' ? 'Sign in' : 'Create account'}
          </button>
        </form>

        <p className="auth-note dim">
          {mode === 'register'
            ? 'The very first account created becomes the owner — that is enforced in SQL, in migration 0001.'
            : 'Wrong password and unknown email return the identical message, on purpose. Ask Module 8 why.'}
        </p>
      </div>

      <TrafficPanel
        title="What just went over the wire"
        empty="Submit the form and the real request and response appear here."
      />
    </div>
  )
}
